import {Task} from "../../tasks/Task.js";
import {WorkSpace} from "./WorkSpace.js";
import {Space} from "./Space.js";
import {TaskList} from "../../tasks/TaskList.js";
import {TimesheetEntry} from "../../timesheet/TimesheetEntry.js";
import {Tag} from "../../app/Tag.js";
import {Folder} from "./Folder.js";

export class ClickUpClient {

    _apiUrl = "";
    _apiKey = "";

    constructor(apiKey, apiUrl="/api/clickup") {
        this._apiUrl = apiUrl;
        this._apiKey = apiKey;
    }

    /**
     * @return {Promise<WorkSpace[]>}
     */
    async getWorkspaces() {

        let response = await fetch(this._getRequest('/team'));
        let data = await response.json();

        /** @type {WorkSpace[]} **/
        let workspaces = [];
        for (let row of data.teams) {
            workspaces.push(new WorkSpace(row));
        }
        return workspaces;
    }

    /**
     *
     * @param {WorkSpace} workspace
     * @return {Promise<Space[]>}
     */
    async getSpaces(workspace) {
        let response = await fetch(this._getRequest('/team/' + workspace.id + '/space?archived=false'));
        let data = await response.json();
        /** @type {Space[]} **/
        let spaces = [];
        for (let row of data.spaces) {
            spaces.push(new Space(row));
        }
        return spaces;
    }

    /**
     * @param {string} spaceId
     * @return {Promise<Folder[]>}
     */
    async getFolders(spaceId) {
        let response = await fetch(this._getRequest('/space/' + spaceId + '/folder?archived=false'));
        let data = await response.json();

        /** @type {Folder[]} */
        let folders = []

        for (let folderData of data.folders) {
            folders.push(new Folder(folderData));
        }

        return folders;
    }

    /**
     *
     * @param spaceId
     * @return {Promise<TaskList[]>}
     */
    async getFolderLessLists(spaceId) {
        let response = await fetch(this._getRequest('/space/' + spaceId + '/list?archived=false'));
        let data = await response.json();
        /** @type {TaskList[]} **/
        let lists = [];
        for (let list of data.lists) {
            lists.push(new TaskList(list));
        }
        return lists;
    }

    /**
     *
     * @param {string} id
     * @return {Promise<TaskList>}
     */
    async getList(id) {
        let response = await fetch(this._getRequest('/list/' + id));
        return new TaskList(await response.json());
    }

    /**
     *
     * @param {string} listId
     * @return {Promise<Task[]>}
     */
    async getTasks(listId) {
        let response = await fetch(this._getRequest('/list/' + listId + '/task?archived=false'));
        let data = await response.json();
        /** @type {Task[]} */
        let tasks = []

        for (let taskData of data.tasks) {
            tasks.push(this._hydrateTask(taskData));
        }

        return tasks;
    }

    /**
     *
     * @param {string} id
     * @return {Promise<?Task>}
     */
    async getTask(id) {
        let response = await fetch(this._getRequest('/task/' + id));
        let data = await response.json();

        if (data) {
            return this._hydrateTask(data);
        }
        return null;
    }

    /**
     *
     * @param {string} workspaceId
     * @param {{}} criteria
     * @return {Promise<Task[]>}
     */
    async findTasks(workspaceId, criteria) {
        let params = new URLSearchParams(criteria);
        let request = this._getRequest('/team/' + workspaceId + '/task' + '?' + params.toString());
        let response = await fetch(request);
        let data = await response.json();

        if (!data || !data.tasks || !data.tasks.length) {
            return Promise.resolve([]);
        }

        /** @type {Task[]} **/
        let tasks = [];
        for (let task of data.tasks) {
            tasks.push(this._hydrateTask(task));
        }

        return Promise.resolve(tasks);
    }

    /**
     * @param {string} workspaceId
     * @param {Date} start
     * @param {Date} end
     * @return {Promise<TimesheetEntry[]>}
     */
    async getTimeEntries(workspaceId, start, end)
    {
        let url = '/team/'+workspaceId+'/time_entries';
        end.setHours(23);
        end.setMinutes(59);
        end.setSeconds(59);
        url += '?start_date=' + start.getTime() + '&end_date=' + end.getTime() + '&include_location_names=true'
        let request = this._getRequest(url);
        let response = await fetch(request);
        let data = await response.json();
        let entries = [];

        for (let entryData of data.data) {
            entries.push(this._hydrateTimesheetEntry(entryData));
        }
        return entries;
    }

    /**
     * @param {string} workspaceId
     * @return {Promise<?TimesheetEntry>}
     */
    async getRunningEntry(workspaceId) {
        let url = '/team/'+workspaceId+'/time_entries/current';
        let request = this._getRequest(url);
        let response = await fetch(request);
        let data = await response.json();
        if (!data.data) {
            return null;
        }
        if (!data.data.task_location || !data.data.task_location.folder_name) {

            let response = await fetch(this._getRequest('/task/' + data.data.task.id));
            let taskData = await response.json();
            if (taskData) {
                data.data.task = taskData;
                data.data.task_location = {
                    "list_id": taskData.list.id,
                    "folder_id": taskData.folder.id,
                    "space_id": taskData.space.id,
                    "list_name": taskData.list.name,
                    "folder_name": taskData.folder.name,
                    "space_name": taskData.space.name
                }
            }
        }
        let entry = this._hydrateTimesheetEntry(data.data);
        entry.isLogged = false;
        return entry;
    }

    /**
     *
     * @param {string} path
     * @return {Request}
     * @private
     */
    _getRequest(path) {
        return new Request(this._apiUrl+path, {
            headers: {
                "Content-Type": "application/json",
                Authorization: this._apiKey
            }
        });
    }

    /**
     * @param data
     * @private
     */
    _hydrateTask(data) {
        let task = new Task();
        task.id = data.id
        task.customId = data.custom_id
        task.name = data.name
        task.orderIndex = data.orderindex
        task.url = data.url;
        if (data.start_date) {
            task.startDate = new Date(parseInt(data.start_date));
        }
        if (data.due_date) {
            task.dueDate = new Date(parseInt(data.due_date));
        }
        if (data.location) {
            task.location = data.location;
        }
        if (!data.custom_fields) {
            return task;
        }
        for (let field of data.custom_fields) {
            let fieldName = field.name.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '');
            task.customMap[fieldName.trim()] = field.value;
        }
        if (data.list && data.list.id) {
            task.parent = new TaskList({id: data.list.id, name: data.list.name});
        }
        if (data.folder && data.folder.id) {
            task.parent.parent = new TaskList({id: data.folder.id, name: data.folder.name});
        }
        return task;
    }

    /**
     * @param {{}} data
     * @return {TimesheetEntry}
     * @private
     */
    _hydrateTimesheetEntry(data) {
        let entry = new TimesheetEntry();
        entry.id = data.id;
        entry.billable = data.billable;
        if (data.start) {
            entry.start = new Date(parseInt(data.start));
        }
        if (data.end) {
            entry.end = new Date(parseInt(data.end));
        }
        entry.description = data.description;
        entry.duration = data.duration !== 0 ? Math.abs(parseInt(data.duration))/1000 : 0;
        entry.source = data.source;

        for(let tagInfo of data.tags) {
            entry.tags.push(new Tag(tagInfo));
        }
        if (data.task) {
            entry.task = this._hydrateTask(data.task);
            if (!entry.task.url) {
                entry.task.url = data.task_url;
            }
        }
        if (data.task_location) {
            if (data.task_location.list_id) {
                entry.task.parent = new TaskList({
                    id: data.task_location.list_id,
                    name: data.task_location.list_name
                });
                if (data.task_location.folder_id) {
                    entry.task.parent.parent = new TaskList({
                        id: data.task_location.folder_id,
                        name: data.task_location.folder_name
                    });
                }
            }
        }

        return entry;
    }
}